import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * AccordionSection — Reusable expand/collapse wrapper.
 * Shows "always visible" content with an optional expandable detail area.
 *
 * Props:
 *   visibleContent: ReactNode (always shown)
 *   expandableContent: ReactNode (shown when expanded)
 *   expandLabel?: string (button text when collapsed, default "Show More")
 *   collapseLabel?: string (button text when expanded, default "Show Less")
 *   defaultOpen?: boolean (default false)
 *   className?: string
 *   testId?: string
 */
const AccordionSection = ({
  visibleContent,
  expandableContent,
  expandLabel = 'Show More',
  collapseLabel = 'Show Less',
  defaultOpen = false,
  className = '',
  testId = 'accordion-section',
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [expandableContent, isOpen]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={className} data-testid={testId}>
      {/* Always visible content */}
      {visibleContent}

      {/* Expandable content with smooth height animation */}
      {expandableContent && (
        <>
          <div
            className="overflow-hidden transition-[max-height] duration-500 ease-in-out"
            style={{ maxHeight: isOpen ? `${contentHeight}px` : '0px' }}
          >
            <div ref={contentRef}>
              {expandableContent}
            </div>
          </div>

          {/* Expand/collapse trigger */}
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-[#0077FF] bg-[#0077FF]/5 hover:bg-[#0077FF]/10 border border-[#0077FF]/20 rounded-full transition-all duration-200"
              data-testid={`${testId}-toggle`}
              aria-expanded={isOpen}
            >
              {isOpen ? collapseLabel : expandLabel}
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AccordionSection;
